/**
 * Блокировка прокрутки страницы, пока открыто модальное окно.
 *
 * Без неё содержимое уезжает под окном: пользователь крутит колесо, ожидая
 * прокрутить список внутри окна, а вместо этого двигается страница позади.
 *
 * Считаем открытые окна, а не запоминаем прежнее значение стиля. Прежняя
 * реализация сохраняла `body.style.overflow` при открытии и возвращала при
 * закрытии — и оставляла страницу заблокированной, если окно исчезало не
 * штатно: при горячей перезагрузке кода, ошибке в отрисовке или когда поверх
 * одного окна открывалось второе. Счётчик такого состояния не допускает:
 * пока он больше нуля — заблокировано, дошёл до нуля — стиль снят полностью.
 */

import { useLayoutEffect } from 'react';

let lockCount = 0;

function applyLock(): void {
  const { body } = document;
  // Ширина полосы прокрутки компенсируется отступом: иначе в момент открытия
  // окна страница дёргается вправо на её ширину.
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  body.style.overflow = 'hidden';
  if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
}

function releaseLock(): void {
  const { body } = document;
  // Стиль снимается целиком, а не возвращается к «прежнему» значению —
  // вернуть можно только то, что кто-то уже успел испортить.
  body.style.removeProperty('overflow');
  body.style.removeProperty('padding-right');
}

export function useBodyScrollLock(active: boolean): void {
  useLayoutEffect(() => {
    if (!active) return;

    lockCount += 1;
    if (lockCount === 1) applyLock();

    return () => {
      lockCount -= 1;
      if (lockCount <= 0) {
        lockCount = 0;
        releaseLock();
      }
    };
  }, [active]);
}

/**
 * Аварийное снятие блокировки.
 *
 * Вызывается при старте приложения: если предыдущая отрисовка оставила
 * страницу заблокированной — например, файл был изменён при открытом окне, —
 * перезагрузка вернёт прокрутку, а не потребует закрывать вкладку.
 */
export function resetBodyScrollLock(): void {
  lockCount = 0;
  releaseLock();
}
